/**
 * AI Milestone Creator Component
 * Conversational AI interface for creating professional milestones
 * Matches the pattern used in AIProjectCreator for consistency
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Paper,
  CircularProgress,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  Alert,
  Divider
} from '@mui/material';
import {
  Send,
  Person,
  SmartToy,
  CheckCircle,
  Edit,
  Close,
  Construction,
  AttachMoney,
  CalendarToday,
  Info
} from '@mui/icons-material';
import milestoneService from '../../services/milestoneService';
import InteractiveSuggestions from './InteractiveSuggestions';
import CostCalculator from './CostCalculator';
import DialogCloseButton from '../common/DialogCloseButton';

const AIMilestoneCreator = ({ open, onClose, projectId, projectDetails, onComplete }) => {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState('welcome');
  const [milestoneData, setMilestoneData] = useState({
    name: '',
    description: '',
    deliverables: [],
    exclusions: [],
    laborCost: 0,
    materialsCost: 0,
    costBreakdown: {},
    laborDetails: {},
    timelineDetails: {},
    additionalNotes: '',
    aiGenerated: true,
    aiSuggestionsUsed: {}
  });
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [costValidation, setCostValidation] = useState(null);
  const [generatedSummary, setGeneratedSummary] = useState('');
  const [draftId, setDraftId] = useState(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize conversation and check for draft
  useEffect(() => {
    if (open && messages.length === 0) {
      initializeConversation();
    }
  }, [open]);

  // Auto-save draft periodically
  useEffect(() => {
    if (open && currentStep !== 'welcome' && currentStep !== 'complete') {
      const timer = setTimeout(() => {
        saveDraft();
      }, 30000); // Save every 30 seconds
      return () => clearTimeout(timer);
    }
  }, [milestoneData, currentStep]);

  const initializeConversation = async () => {
    addAIMessage(`Hi! I'm your AI milestone assistant. 🏗️ I'll help you create a professional, detailed milestone contract for your project.`);
    
    setIsLoading(true);
    try {
      // Check for existing draft
      const response = await milestoneService.getDraft(projectId);
      if (response.success && response.data) {
        const draft = response.data;
        setMilestoneData(draft.milestone_data);
        setDraftId(draft.id);
        
        setTimeout(() => {
          addAIMessage(`I found a draft you started earlier. Would you like to continue where you left off, or start fresh?`);
          setCurrentStep('resume_draft');
        }, 1500);
      } else {
        startFreshConversation();
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
      startFreshConversation();
    } finally {
      setIsLoading(false);
    }
  };

  const startFreshConversation = () => {
    setTimeout(() => {
      addAIMessage(`This will take about 5-7 minutes. I'll ask you questions one at a time, and use AI to suggest items you might have missed and validate your costs.\n\nLet's start! What would you like to call this milestone?`);
      addAIMessage(`💡 Examples: "Foundation Work", "Electrical Installation", "Roof Construction"`);
      setCurrentStep('milestone_name');
    }, 1500);
  };

  const addAIMessage = (text, component = null) => {
    setMessages(prev => [...prev, {
      id: Date.now() + Math.random(),
      sender: 'ai',
      text,
      component,
      timestamp: new Date()
    }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      sender: 'user',
      text,
      timestamp: new Date()
    }]);
  };

  const saveDraft = async () => {
    if (!projectId) return;
    
    try {
      await milestoneService.saveDraft(projectId, milestoneData, currentStep);
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
  };

  const handleSend = async () => {
    if (!userInput.trim() || isLoading) return;

    const input = userInput.trim();
    addUserMessage(input);
    setUserInput('');
    setIsLoading(true);

    try {
      await processStep(input);
    } catch (error) {
      console.error('Error processing step:', error);
      addAIMessage('Sorry, I encountered an error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const processStep = async (input) => {
    switch (currentStep) {
      case 'resume_draft':
        if (input.toLowerCase().includes('continue') || input.toLowerCase().includes('yes')) {
          addAIMessage(`Great! Let's continue from where you left off.`);
          // Resume from the saved step
          setTimeout(() => {
            addAIMessage(`Your milestone is called "${milestoneData.name}". Let's continue building it out.`);
            setCurrentStep('description');
          }, 1000);
        } else {
          addAIMessage(`No problem! Let's start fresh.`);
          setMilestoneData({
            name: '',
            description: '',
            deliverables: [],
            exclusions: [],
            laborCost: 0,
            materialsCost: 0,
            costBreakdown: {},
            laborDetails: {},
            timelineDetails: {},
            additionalNotes: '',
            aiGenerated: true,
            aiSuggestionsUsed: {}
          });
          startFreshConversation();
        }
        break;

      case 'milestone_name':
        setMilestoneData(prev => ({ ...prev, name: input }));
        addAIMessage(`Perfect! "${input}" it is. ✅`);
        setTimeout(() => {
          addAIMessage(`Now, describe what work will be done in this milestone. Be as detailed as possible - this helps me suggest items you might have missed.`);
          addAIMessage(`💡 Include: specific tasks, materials, methods, quality standards, etc.`);
          setCurrentStep('description');
        }, 1500);
        break;

      case 'description':
        setMilestoneData(prev => ({ ...prev, description: input }));
        addAIMessage(`Got it! Let me analyze that... 🤖`);
        
        // Call AI to analyze scope
        try {
          const analysis = await milestoneService.analyzeScope({
            projectId,
            milestoneName: milestoneData.name,
            description: input,
            projectType: projectDetails?.type || 'general',
            location: projectDetails?.location || 'Zimbabwe'
          });

          if (analysis.success) {
            setAiAnalysis(analysis.data);
            
            setTimeout(() => {
              addAIMessage(`I've analyzed your description and identified these key items:`);
              addAIMessage(``, <InteractiveSuggestions 
                suggestions={analysis.data.suggestedItems || []}
                onSelectionChange={(selected) => {
                  setMilestoneData(prev => ({
                    ...prev,
                    aiSuggestionsUsed: { ...prev.aiSuggestionsUsed, scope: selected }
                  }));
                }}
              />);
              
              setTimeout(() => {
                addAIMessage(`Would you like to add any of these to your milestone? You can select them above, or type "skip" to continue.`);
                setCurrentStep('suggestions_review');
              }, 1500);
            }, 2000);
          }
        } catch (error) {
          console.error('AI analysis failed:', error);
          addAIMessage(`I couldn't analyze that right now, but let's continue. What are the main deliverables for this milestone?`);
          setCurrentStep('deliverables');
        }
        break;

      case 'suggestions_review':
        addAIMessage(`Great! Now, what are the specific deliverables? What will the client receive when this milestone is complete?`);
        addAIMessage(`💡 Examples: "Completed foundation with waterproofing", "All electrical wiring installed and tested", "Concrete test certificates"`);
        setCurrentStep('deliverables');
        break;

      case 'deliverables':
        const deliverables = input.split('\n').filter(d => d.trim());
        setMilestoneData(prev => ({ ...prev, deliverables }));
        addAIMessage(`Perfect! I've noted ${deliverables.length} deliverable(s). ✅`);
        setTimeout(() => {
          addAIMessage(`Now, what's NOT included in this milestone? (Exclusions help prevent disputes later)`);
          addAIMessage(`💡 Examples: "Materials not included", "Site cleanup", "Painting", or type "none" if nothing is excluded`);
          setCurrentStep('exclusions');
        }, 1500);
        break;

      case 'exclusions':
        const exclusions = input.toLowerCase() === 'none' ? [] : input.split('\n').filter(e => e.trim());
        setMilestoneData(prev => ({ ...prev, exclusions }));
        addAIMessage(`Got it! ${exclusions.length > 0 ? `${exclusions.length} exclusion(s) noted.` : 'No exclusions.'} ✅`);
        setTimeout(() => {
          addAIMessage(`Now let's talk about costs. 💰 How would you like to structure your pricing?`);
          addAIMessage(``, <CostCalculator 
            milestoneName={milestoneData.name}
            location={projectDetails?.location}
            onCostSubmit={(costData) => {
              setMilestoneData(prev => ({
                ...prev,
                laborCost: costData.laborCost,
                materialsCost: costData.materialsCost || 0,
                costBreakdown: costData.breakdown,
                laborDetails: costData.laborDetails
              }));
              setCostValidation(costData.validation);
              
              addAIMessage(`Costs recorded! Labor: $${costData.laborCost}, Materials: $${costData.materialsCost || 0}, Total: $${parseFloat(costData.laborCost) + parseFloat(costData.materialsCost || 0)}`);
              
              if (costData.validation && !costData.validation.isReasonable) {
                addAIMessage(`⚠️ ${costData.validation.marketComparison}`);
              }
              
              setTimeout(() => {
                addAIMessage(`When will this milestone start and finish? Please provide dates.`);
                setCurrentStep('timeline');
              }, 1500);
            }}
          />);
          setCurrentStep('costing');
        }, 1500);
        break;

      case 'timeline':
        // Parse timeline from user input
        setMilestoneData(prev => ({ ...prev, timelineDetails: { raw: input } }));
        addAIMessage(`Timeline noted! ✅`);
        setTimeout(() => {
          addAIMessage(`Any additional notes or special requirements? (Or type "none")`);
          setCurrentStep('additional_notes');
        }, 1500);
        break;

      case 'additional_notes':
        const notes = input.toLowerCase() === 'none' ? '' : input;
        setMilestoneData(prev => ({ ...prev, additionalNotes: notes }));
        addAIMessage(`Perfect! Let me generate a professional summary of this milestone... 🤖`);
        
        // Generate summary
        try {
          const summaryResponse = await milestoneService.generateSummary({
            name: milestoneData.name,
            description: milestoneData.description,
            deliverables: milestoneData.deliverables,
            exclusions: milestoneData.exclusions,
            laborCost: milestoneData.laborCost,
            materialsCost: milestoneData.materialsCost,
            timeline: milestoneData.timelineDetails,
            additionalNotes: notes
          });

          if (summaryResponse.success) {
            setGeneratedSummary(summaryResponse.data.summary);
            
            setTimeout(() => {
              addAIMessage(`Here's your professional milestone summary:`);
              addAIMessage(summaryResponse.data.summary);
              
              setTimeout(() => {
                addAIMessage(`Does this look good? Type "yes" to create the milestone and send it to the client, or "edit" to make changes.`);
                setCurrentStep('confirm');
              }, 2000);
            }, 2000);
          }
        } catch (error) {
          console.error('Summary generation failed:', error);
          addAIMessage(`I couldn't generate a summary right now. Would you like to create the milestone anyway? (yes/no)`);
          setCurrentStep('confirm');
        }
        break;

      case 'confirm':
        if (input.toLowerCase().includes('yes')) {
          addAIMessage(`Creating your milestone and sending it to the client... 📤`);
          
          try {
            // Create milestone
            const createResponse = await milestoneService.createAIMilestone({
              projectId,
              ...milestoneData
            });

            if (createResponse.success) {
              const milestoneId = createResponse.data.milestoneId;
              
              // Submit to client
              await milestoneService.submitToClient(milestoneId);
              
              addAIMessage(`✅ Success! Your milestone has been created and sent to the client for approval.`);
              addAIMessage(`They'll receive an email notification and can review it in their dashboard.`);
              
              setCurrentStep('complete');
              
              setTimeout(() => {
                if (onComplete) {
                  onComplete(milestoneId);
                }
              }, 3000);
            }
          } catch (error) {
            console.error('Milestone creation failed:', error);
            addAIMessage(`❌ Sorry, I couldn't create the milestone. Please try again or contact support.`);
          }
        } else {
          addAIMessage(`No problem! What would you like to change? You can say things like "change the cost" or "edit deliverables".`);
          setCurrentStep('edit');
        }
        break;

      default:
        addAIMessage(`I'm not sure what to do with that. Can you try again?`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogCloseButton onClick={onClose} />
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Construction sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">AI Milestone Creator</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
            {messages.map((msg) => (
              <Fade key={msg.id} in timeout={300}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                    mb: 2
                  }}
                >
                  {msg.sender === 'ai' && (
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                      <SmartToy />
                    </Avatar>
                  )}
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      bgcolor: msg.sender === 'user' ? 'primary.light' : 'grey.100'
                    }}
                  >
                    {msg.text && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {msg.text}
                      </Typography>
                    )}
                    {msg.component}
                  </Paper>
                  {msg.sender === 'user' && (
                    <Avatar sx={{ bgcolor: 'secondary.main', ml: 1 }}>
                      <Person />
                    </Avatar>
                  )}
                </Box>
              </Fade>
            ))}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          {currentStep !== 'complete' && currentStep !== 'costing' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                maxRows={3}
                placeholder="Type your answer..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={isLoading}
              />
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={isLoading || !userInput.trim()}
                sx={{ minWidth: '56px' }}
              >
                <Send />
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AIMilestoneCreator;
